import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Container, Grid, IconButton, ImageList, ImageListItem, InputAdornment, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import Loader from '../common/loader/loader';
import PhenBrain from '../common/images/phen-brain.gif'; 
import { toast } from 'react-toastify';
import Styles from '../common/styles/styles';
import LightBulbIcon from '@mui/icons-material/Lightbulb';
import PersonIcon from '@mui/icons-material/Person';
import PhenInteractionIcon from '../common/images/phen-interaction.gif';
import { useUser } from '../../userContext';

export default function PhenTalk() { 

    const { ccgEmployee } = useUser();
    const phenTextStyle = Styles.phenTextStyle;

    const [formData, setFormData] = useState({
        question: '',
        answer: ''
    })

    const [emoji, setEmoji] = useState({
        icon: ''
    })

    const [history, setHistory] = useState([]);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState(null);

    const [phenResponse, setPhenResponse] = useState({
        emotion: '',
        emoji: '', 
        confidence: '', 
        answer: '', 
        duration: ''
    })

    const [processingRequesting, setProcessingRequesting] = useState(false);

    const abortControllerRef = useRef(null);
    
    const answersRef = useRef(null);
    const defaultAnswer = "Hello! I am the PhenTalk chat! I am your Cyber SME and CheckMate system administrator. Please ask me anything.";    

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && event.shiftKey) {
            event.preventDefault();

            const question = event.target.value;
            const start = event.target.selectionStart;
            const end = event.target.selectionEnd;
            
            const newValue = question.substring(0, start) + '\n' + question.substring(end);
            setFormData({ ...formData, question: newValue });
            
            setTimeout(() => {
                event.target.setSelectionRange(start + 1, start + 1);
            }, 0);
        } else if (event.key === 'Enter' ) {
            event.preventDefault(); 
            handleSubmit(event);
        }
    }

    const handleInputChange = (field) => (e) => {
        let value = e.target.value;
        setFormData({ ...formData, [field]: value });
    }

    const handleClear = () => {
        setFormData({ ...formData, question: '' });
    }

    const handleClearAll = () => {
        setFormData({ ...formData, question: '', answer: '' });
        setHistory([]);
        setEmoji({ ...emoji, icon: '' });
    }

    const handleSubmit = async (event) => {
        if (formData.question.trim() !== '') {
            await submitQuestion(event);      
        }
    }

    const handleCancelPost = async () => {
        if (abortControllerRef.current) {
           abortControllerRef.current.abort();
        }
    }

    const submitQuestion = async (event) => {

        event.preventDefault();

        setProcessingRequesting(true);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        let result = null;

        if (formData.answer === defaultAnswer) {
            setFormData({ ...formData, answer: '' });
        }

        // Creates a timeout that aborts the query after a specified period of time in the env file
        const timeout = process.env.REACT_APP_API_TIMEOUT || '180000';
        const timeoutSignal = AbortSignal.timeout(timeout);

        const abortSignal = AbortSignal.any([
            controller.signal,
            timeoutSignal
        ])

            
        try {
            const startTime = performance.now();
            const response = await fetch(`${process.env.REACT_APP_API}/phen/submitQuestion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                signal: abortSignal,
            });

            const answer = await response.json();
            const endTime = performance.now(); 
            const duration = endTime - startTime; 
            if (answer.data !== undefined) {
                answer.data.duration = duration.toFixed(2);
                const resp = answer.data;
                buildResponse(resp);
                setLoading(false);
            } else {                
                setLoading(false);
                toast.error(result.message);
            }            
        } catch (error) {
            if (error.name === 'TimeoutError') {
                toast.error('Request Timed Out Due To The Server Taking Too Long Too Respond.');
            } else if (error.name === 'AbortError') {
                toast.success('Request Succesfully Aborted By User');
            } else {
                toast.error('Network error.  Please try again: ', error);
                return false;
            }
            setLoading(false);
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setProcessingRequesting(false);
        }
    }
    
    const buildResponse = (incResp) => {

        let response = '';
        if (formData.answer !== defaultAnswer) {
            response = formData.answer;
        }

        setPhenResponse({ ...phenResponse, emotion: incResp.emotion, emoji: incResp.icon, confidence: incResp.confidence, answer: incResp.response, duration: incResp.duration });
        if (response !== '') {            
            response += `\n\n`;
        } 
        
        if (ccgEmployee) {
            incResp.response += ` (${incResp.duration} ms)`;
        }
        response += `${formData.question}\n\t${incResp.response}\n\n`;
        

        setFormData({ ...formData, question: '', answer: response.trim() });

        setEmoji({ ...emoji, icon: emoji.icon += incResp["icon"].trim() });

        setHistory([
            ...history, 
            { type: 'question', text: formData.question },
            { type: 'answer', text: incResp.response.trim() }
        ]);
    }

    const scrollToBottom = () => {
        const boxarea = answersRef.current;
        if (boxarea) {
            boxarea.scrollTop = boxarea.scrollHeight;
        }
    }
    
    useEffect(() => {
        scrollToBottom();
        if ( formData.question === '' && formData.answer === '') {
            setFormData({ ...formData, answer: defaultAnswer });
        }
    }, [formData]);

    return (
        <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
            <Box sx={{pt: 2}}>
                <ClassificationBannerHeader></ClassificationBannerHeader>
                <PageHeader title="PhenTalk"></PageHeader>
            </Box>

            <Grid container spacing={2}>
                <Grid size={3}>
                    {
                        loading ? 
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    height: '100vh' 
                                }}
                                >
                                    <Loader size={80} /> 
                            </Box>
                            
                        : 
                            <>
                                <Box component="form" onSubmit={handleSubmit}                    
                                    sx={{ marginTop: '50px', backgroundColor: '#000' }}
                                >
                                    <ImageList sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                                        <ImageListItem>
                                            <img
                                                src={PhenBrain}
                                                alt="PhenBrain"
                                                loading="lazy"
                                                style={{ width: '400px', height: 'auto' }}
                                            />
                                        </ImageListItem>
                                    </ImageList>
                                </Box>
                                <Box sx={{ 
                                            marginTop: '-50px', 
                                            backgroundColor: '#000', 
                                            display: 'flex',
                                        }}                         
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <TextField
                                        sx={{ textAlign: 'center', alignItems: 'center', mr: 5, width: 300 }} 
                                        value={emoji.icon}
                                        multiline
                                        rows={7}
                                        slotProps={{ 
                                            readOnly: true, 
                                        }}
                                        InputProps={{
                                            inputProps: { 
                                                style: { textAlign: 'center' }
                                            },
                                            readOnly: true
                                        }}
                                    />
                                </Box>
                            </>
                        }
                </Grid>
                <Grid size={9}>

                    <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="h4" component="div" sx={{ mt: 1 }}>
                            PhenTalk - Performing As Your Chatbot Today
                        </Typography>
                    </Box>

                    <Box sx={{ backgroundColor: '#1A2027', minHeight: '80px' }}>

                        <TextField 
                            variant="outlined" 
                            label="Question"
                            value={formData.question}
                            onChange={handleInputChange('question')}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            rows={2}
                            sx={ phenTextStyle }
                            multiline
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment 
                                            position="start"
                                            sx={{ 
                                                alignSelf: 'flex-start',
                                            }} 
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                <Tooltip title="Select Shift + Enter For A New Line"  >
                                                    <LightBulbIcon fontSize="small" sx={{ color: '#FDD835' }} />
                                                </Tooltip>
                                            </span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                    </Box>
                    
                    <ButtonGroup sx={{ marginTop: '10px' }} >
                        { !processingRequesting && (
                            <>
                                <Button sx={{ marginRight: '15px', color: '#FFF', borderColor: '#1A2027', borderRightColor: '#1A2027', borderStyle: 'solid' }}
                                    onClick={handleClear}> Clear Question
                                </Button>
                                <Button sx={{ marginRight: '15px', color: '#FFF', borderColor: '#1A2027', borderRightColor: '#1A2027', borderStyle: 'solid' }}
                                    onClick={handleClearAll}> Clear All
                                </Button>
                                <Button 
                                    endIcon={
                                        <Tooltip title="Click Here, Or Select Enter To Submit" >
                                                <LightBulbIcon fontSize="small" sx={{ color: '#FDD835' }} />
                                        </Tooltip>
                                    }
                                    type="submit"
                                    sx={{ marginRight: '15px', color: '#FFF', borderColor: '#1A2027', borderRightColor: '#1A2027', borderStyle: 'solid' }}
                                    onClick={handleSubmit}> Submit
                                </Button>
                            </>
                        )}
                        { processingRequesting && (
                            <Button 
                                
                                sx={{ marginRight: '15px', color: '#FFF', borderColor: '#1A2027', borderRightColor: '#1A2027', borderStyle: 'solid' }}
                                onClick={() => handleCancelPost()}>Cancel
                            </Button>
                        )}
                    </ButtonGroup>
                   <Box ref={answersRef} sx={{ border: '2px solid', borderColor: 'white', borderRadius: 2, p: 1, backgroundColor: '#1A2027', marginTop: '25px', minHeight: '300px', maxHeight: '300px', overflowY: 'auto' }} >
                        {history.length === 0 ? (
                            <Typography sx={{ color: "white" }}>
                                Hello! I am the PhenTalk chat! I am your Cyber SME and CheckMate system administrator. Please ask me anything.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {history.map((entry, index) => {
                                    const isQuestion = entry.type === 'question';
                                    const isAnswer = entry.type === 'answer';
                                    return (
                                        <Box 
                                            key={index} 
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: isQuestion ? 'flex-end' : 'flex-start', 
                                                alignItems: 'center',
                                                gap: 1,
                                                maxHeight: '375px',
                                                overFlowY: 'auto',
                                            }}
                                        >
                                            {isAnswer && (
                                                <img
                                                    src={ PhenInteractionIcon }
                                                    alt="PhenIcon"
                                                    loading="lazy"
                                                    style={{ width: '20px', height: 'auto' }}
                                                />
                                            )}

                                            <Paper 
                                                elevation={0}
                                                sx={{ 
                                                    p: .5,
                                                    overFlowY: 'auto',
                                                    bgcolor: isQuestion ? 'primary.main' : 'background.paper',
                                                    borderRadius: 2
                                                }}
                                            >
                                                <Typography
                                                    sx={{ 
                                                            flex: 1,
                                                            minWidth: '0px',
                                                            maxWidth: '950px',
                                                            maxHeight: '150px',
                                                            overflowY: 'auto',
                                                            textAlign: isQuestion ? 'right' : 'left',
                                                            color: isQuestion ? 'white' : 'black',
                                                            "& fieldset": { border: 'none' },
                                                            whiteSpace: 'pre-line', 
                                                    }}
                                                    m={.5}
                                                >
                                                    {entry.text ? entry.text.replaceAll('\\n', '\n') : ''}
                                                </Typography>
                                            </Paper>

                                            {isQuestion && (
                                                <PersonIcon sx={{ color: 'white' }} />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '20px'}}>
                        <Typography sx={{fontSize: '.8rem', marginBottom: '-20px'}}>
                            PhenTalk can make mistakes. Check important info.
                        </Typography>
                    </Box>
                </Grid>
                
            </Grid>
            
            
        </Container>
    )
}
